import React, { useState, useEffect } from "react";
import {
    Box, Card, CardContent, TextField, Typography, Snackbar, Alert,
    Switch, FormControlLabel, Table, TableBody, TableCell,
    TableHead, TableRow, Button, Divider, IconButton, Tooltip, Chip,
} from "@mui/material";
import RequestQuoteRoundedIcon  from "@mui/icons-material/RequestQuoteRounded";
import PaymentRoundedIcon       from "@mui/icons-material/PaymentRounded";
import StorefrontRoundedIcon    from "@mui/icons-material/StorefrontRounded";
import TrendingUpRoundedIcon    from "@mui/icons-material/TrendingUpRounded";
import ShieldRoundedIcon        from "@mui/icons-material/ShieldRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ArrowBackRoundedIcon     from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon          from "@mui/icons-material/EditRounded";
import SaveRoundedIcon          from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon         from "@mui/icons-material/CloseRounded";
import PercentRoundedIcon       from "@mui/icons-material/PercentRounded";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";
import { useNavigate } from "react-router-dom";
import EngineVersionCard from "../pricing/EngineVersionCard.js";

// ── Brand palette ────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    emerald: "#059669", emeraldLt:"#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    bg:      "#F7FAFC", border:   "#E2E8F0",
    text:    "#0F172A", muted:    "#64748B",
};

const DEFAULT_DEMAND = {
    demand_pricing_enabled: 1,
    demand_occ_tier2: 40,   demand_occ_mult_t2: 1.15,
    demand_occ_tier3: 70,   demand_occ_mult_t3: 1.30,
    demand_occ_tier4: 90,   demand_occ_mult_t4: 1.50,
    demand_time_tier2_h: 168, demand_time_mult_t2: 1.05,
    demand_time_tier3_h: 72,  demand_time_mult_t3: 1.15,
    demand_time_tier4_h: 24,  demand_time_mult_t4: 1.25,
};

// ── Section card ─────────────────────────────────────────────────────────
const SCard = ({ accent = T.teal, children }) => (
    <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
        <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />
        <CardContent sx={{ p:2.5, "&:last-child":{ pb:2.5 } }}>{children}</CardContent>
    </Card>
);

// ── Section header row ────────────────────────────────────────────────────
const SectionHeader = ({ icon, iconBg, iconColor, title, subtitle, editMode, onEdit, onSave, onCancel, saving }) => (
    <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", mb:2 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
            <Box sx={{ width:36,height:36,borderRadius:"10px",bgcolor:iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                {React.cloneElement(icon, { sx:{ fontSize:18,color:iconColor } })}
            </Box>
            <Box>
                <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{title}</Typography>
                {subtitle && <Typography sx={{ fontSize:12,color:T.muted,mt:0.2,lineHeight:1.4 }}>{subtitle}</Typography>}
            </Box>
        </Box>
        <Box sx={{ display:"flex",gap:0.8,flexShrink:0,ml:1,mt:0.3 }}>
            {!editMode ? (
                <Tooltip title="Edit">
                    <IconButton size="small" onClick={onEdit}
                        sx={{ color:T.amber,bgcolor:T.amberLt,borderRadius:"8px","&:hover":{ bgcolor:"#FDE68A" } }}>
                        <EditRoundedIcon sx={{ fontSize:15 }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <>
                    <Button size="small" variant="contained" disableElevation onClick={onSave} disabled={saving}
                        startIcon={<SaveRoundedIcon sx={{ fontSize:13 }} />}
                        sx={{ borderRadius:"9px",textTransform:"none",fontWeight:700,fontSize:12,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk },px:1.5 }}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button size="small" variant="outlined" onClick={onCancel} disabled={saving}
                        startIcon={<CloseRoundedIcon sx={{ fontSize:13 }} />}
                        sx={{ borderRadius:"9px",textTransform:"none",fontWeight:600,fontSize:12,borderColor:T.border,color:T.muted,px:1.5 }}>
                        Cancel
                    </Button>
                </>
            )}
        </Box>
    </Box>
);

// ── Big stat pill (view mode) ─────────────────────────────────────────────
const StatPill = ({ value, unit }) => (
    <Box sx={{ display:"inline-flex",alignItems:"baseline",gap:0.5,
        px:2,py:1,bgcolor:T.tealLt,borderRadius:"12px",border:`1px solid ${T.teal}33` }}>
        <Typography sx={{ fontSize:28,fontWeight:900,color:T.teal,fontFamily:"'Plus Jakarta Sans',sans-serif",lineHeight:1 }}>{value||"—"}</Typography>
        <Typography sx={{ fontSize:14,fontWeight:700,color:T.tealDk }}>{unit}</Typography>
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────
function BillingSettings() {
    const [platformFee,    setPlatformFee]    = useState("");
    const [gstFee,         setGstFee]         = useState("");
    const [editModePlatform, setEditModePlatform] = useState(false);
    const [editModeGst,    setEditModeGst]    = useState(false);

    const [floorPrice,     setFloorPrice]     = useState("");
    const [ceilingPrice,   setCeilingPrice]   = useState("");
    const [editModeGuardrail, setEditModeGuardrail] = useState(false);

    const [ownerPricingEnabled,  setOwnerPricingEnabled]  = useState(true);
    const [platformMinMargin,    setPlatformMinMargin]     = useState("5");
    const [editModeOwner,        setEditModeOwner]         = useState(false);

    const [demandEnabled,  setDemandEnabled]  = useState(true);
    const [demandConfig,   setDemandConfig]   = useState({ ...DEFAULT_DEMAND });

    const [cargoConfig,    setCargoConfig]    = useState({
        cargo_fragile_surcharge_pct: 10,
        cargo_hazardous_surcharge_pct: 17,
        cargo_perishable_rate_per_km: 10,
    });
    const [editModeCargo,  setEditModeCargo]  = useState(false);

    const [minFareWeightThreshold, setMinFareWeightThreshold] = useState("");
    const [minFareAmount,          setMinFareAmount]           = useState("");
    const [editModeMinFare,        setEditModeMinFare]         = useState(false);

    // saved copies for cancel
    const [savedPlatformFee, setSavedPlatformFee] = useState("");
    const [savedGstFee,      setSavedGstFee]      = useState("");
    const [savedMinFareWeight, setSavedMinFareWeight] = useState("");
    const [savedMinFareAmount, setSavedMinFareAmount] = useState("");

    const [engineVersion,  setEngineVersion]  = useState(1);
    const [reloadKey,      setReloadKey]      = useState(0);
    const [loading,        setLoading]        = useState(true);
    const [saving,         setSaving]         = useState(false);
    const navigate = useNavigate();

    const [snackbar, setSnackbar] = useState({ open:false, message:"", severity:"success" });
    const toast = (message, severity="success") => setSnackbar({ open:true, message, severity });

    const formatFee = (value) => {
        if (value == null || value === "") return "";
        const num = parseFloat(value);
        if (isNaN(num)) return "";
        return Number.isInteger(num) ? num.toString() : parseFloat(num.toFixed(2)).toString();
    };

    useEffect(() => {
        const fetchCharges = async () => {
            setLoading(true);
            try {
                const res = await getData(API_ENDPOINTS.getAllCharges);
                if (res?.statusCode === 200) {
                    const charges = res.message.data;
                    const find = (key) => charges.find((c) => c.key === key);

                    const pf = formatFee(find("platform_fee")?.value_numeric);
                    const gst = formatFee(find("gst_percent")?.value_numeric);
                    setPlatformFee(pf); setSavedPlatformFee(pf);
                    setGstFee(gst);     setSavedGstFee(gst);

                    const floorRow   = find("price_floor_per_km");
                    const ceilingRow = find("price_ceiling_per_km");
                    if (floorRow)   setFloorPrice(formatFee(floorRow.value_numeric));
                    if (ceilingRow) setCeilingPrice(formatFee(ceilingRow.value_numeric));

                    const ownerEnabled = find("owner_pricing_enabled");
                    const minMargin    = find("platform_min_margin_percent");
                    if (ownerEnabled) setOwnerPricingEnabled(ownerEnabled.value_numeric !== 0);
                    if (minMargin)    setPlatformMinMargin(formatFee(minMargin.value_numeric));

                    const demandEnabledRow = find("demand_pricing_enabled");
                    if (demandEnabledRow) setDemandEnabled(demandEnabledRow.value_numeric !== 0);

                    const patch = {};
                    Object.keys(DEFAULT_DEMAND).filter(k=>k!=="demand_pricing_enabled").forEach(key => {
                        const row = find(key);
                        if (row) patch[key] = row.value_numeric;
                    });
                    setDemandConfig(prev => ({ ...prev, ...patch }));

                    const mfw = formatFee(find("platform_min_fare_weight_threshold")?.value_numeric);
                    const mfa = formatFee(find("platform_min_fare_amount")?.value_numeric);
                    setMinFareWeightThreshold(mfw); setSavedMinFareWeight(mfw);
                    setMinFareAmount(mfa);           setSavedMinFareAmount(mfa);

                    const cargoPatch = {};
                    ["cargo_fragile_surcharge_pct","cargo_hazardous_surcharge_pct","cargo_perishable_rate_per_km"].forEach(key => {
                        const row = find(key);
                        if (row) cargoPatch[key] = row.value_numeric;
                    });
                    setCargoConfig(prev => ({ ...prev, ...cargoPatch }));

                    const engineRow = find("pricing_engine_version");
                    setEngineVersion(engineRow ? Number(engineRow.value_numeric) : 1);
                } else {
                    toast("Failed to fetch billing data","error");
                }
            } catch(e) {
                console.error(e);
                toast("Failed to fetch billing data","error");
            } finally {
                setLoading(false);
            }
        };
        fetchCharges();
    }, [reloadKey]);

    const handleSave = async (key, value, setEditMode) => {
        setSaving(true);
        try {
            const existingRes = await getData(API_ENDPOINTS.getAllCharges);
            const charges = existingRes?.statusCode === 200 ? existingRes.message.data || [] : [];
            const existing = charges.find((c) => c.key === key);
            const res = existing
                ? await postData(API_ENDPOINTS.updateCharge, { key, value_numeric: parseFloat(value) })
                : await postData(API_ENDPOINTS.generateCharges, { key, value_numeric: parseFloat(value), value_text:"" });
            if (res.statusCode === 200) { toast(`${key} saved successfully`); setEditMode(false); }
            else toast(`Failed to save ${key}`,"error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const handleSaveMinFare = async () => {
        setSaving(true);
        try {
            const charges = [
                { key:"platform_min_fare_weight_threshold", value_numeric: parseFloat(minFareWeightThreshold)||0 },
                { key:"platform_min_fare_amount",           value_numeric: parseFloat(minFareAmount)||0 },
            ];
            const res = await postData(API_ENDPOINTS.bulkUpsertCharges, { charges });
            if (res?.statusCode === 200) {
                setSavedMinFareWeight(minFareWeightThreshold); setSavedMinFareAmount(minFareAmount);
                toast("Minimum fare settings saved"); setEditModeMinFare(false);
            } else toast("Failed to save minimum fare settings","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const handleSaveGuardrails = async () => {
        setSaving(true);
        try {
            const charges = [];
            if (floorPrice !== "")   charges.push({ key:"price_floor_per_km",   value_numeric: parseFloat(floorPrice) });
            if (ceilingPrice !== "") charges.push({ key:"price_ceiling_per_km",  value_numeric: parseFloat(ceilingPrice) });
            if (!charges.length) { toast("Enter at least one guardrail value","warning"); setSaving(false); return; }
            const res = await postData(API_ENDPOINTS.bulkUpsertCharges, { charges });
            if (res?.statusCode === 200) { toast("Guardrails saved"); setEditModeGuardrail(false); }
            else toast("Failed to save guardrails","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const handleSaveOwnerPricing = async () => {
        setSaving(true);
        try {
            const charges = [
                { key:"owner_pricing_enabled",       value_numeric: ownerPricingEnabled ? 1 : 0 },
                { key:"platform_min_margin_percent", value_numeric: parseFloat(platformMinMargin)||5 },
            ];
            const res = await postData(API_ENDPOINTS.bulkUpsertCharges, { charges });
            if (res?.statusCode === 200) { toast("Owner pricing settings saved"); setEditModeOwner(false); }
            else toast("Failed to save owner pricing settings","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const handleSaveCargoConfig = async () => {
        setSaving(true);
        try {
            const charges = Object.entries(cargoConfig).map(([key,value]) => ({ key, value_numeric: parseFloat(value)||0 }));
            const res = await postData(API_ENDPOINTS.bulkUpsertCharges, { charges });
            if (res?.statusCode === 200) { toast("Cargo surcharge rates saved"); setEditModeCargo(false); }
            else toast("Failed to save cargo surcharge rates","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const handleSaveDemandConfig = async () => {
        setSaving(true);
        try {
            const charges = [
                { key:"demand_pricing_enabled", value_numeric: demandEnabled ? 1 : 0 },
                ...Object.entries(demandConfig)
                    .filter(([k]) => k !== "demand_pricing_enabled")
                    .map(([key,value]) => ({ key, value_numeric: parseFloat(value)||0 })),
            ];
            const res = await postData(API_ENDPOINTS.bulkUpsertCharges, { charges });
            if (res?.statusCode === 200) toast("Demand pricing config saved");
            else toast("Failed to save demand config","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const demandField = (key) => (
        <TextField size="small" type="number" value={demandConfig[key]??""} inputProps={{ step:"any" }}
            onChange={e => setDemandConfig(p=>({...p,[key]:e.target.value}))}
            InputProps={{ sx:{ borderRadius:"9px",fontSize:13 } }} sx={{ width:90 }} />
    );

    return (
        <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>
            <LoadingSpinner isLoading={loading || saving} />

            {/* ── Hero header ── */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
                borderRadius:"20px", p:3, mb:3,
                display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
                boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
                    <IconButton onClick={()=>navigate("/app/settings")}
                        sx={{ bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",borderRadius:"10px",color:"#fff","&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
                        <ArrowBackRoundedIcon sx={{ fontSize:18 }} />
                    </IconButton>
                    <Box sx={{ width:44,height:44,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                        <RequestQuoteRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                            Fee Structure & Taxation
                        </Typography>
                        <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
                            Platform fee, GST, demand pricing, owner margins, and pricing engine settings
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display:"flex",gap:1,flexWrap:"wrap" }}>
                    <Chip label={`Platform Fee: ${platformFee||"—"}%`} size="small"
                        sx={{ bgcolor:"rgba(255,255,255,0.18)",color:"#fff",fontWeight:700,fontSize:11,border:"1px solid rgba(255,255,255,0.3)" }} />
                    <Chip label={`GST: ${gstFee||"—"}%`} size="small"
                        sx={{ bgcolor:"rgba(255,255,255,0.18)",color:"#fff",fontWeight:700,fontSize:11,border:"1px solid rgba(255,255,255,0.3)" }} />
                </Box>
            </Box>

            {/* ── Pricing engine version card ── */}
            <Box sx={{ mb:2 }}>
                <EngineVersionCard value={engineVersion} onSaved={()=>setReloadKey(k=>k+1)} />
            </Box>

            {/* ── Platform Fee + GST (2-column) ── */}
            <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr", md:"1fr 1fr" }, gap:2, mb:2 }}>

                {/* Platform Fee */}
                <SCard accent={T.teal}>
                    <SectionHeader
                        icon={<RequestQuoteRoundedIcon />} iconBg={T.tealLt} iconColor={T.teal}
                        title="Platform Fee"
                        subtitle="Service fee charged by the platform on every booking."
                        editMode={editModePlatform}
                        onEdit={()=>setEditModePlatform(true)}
                        onSave={()=>handleSave("platform_fee",platformFee,setEditModePlatform)}
                        onCancel={()=>{ setPlatformFee(savedPlatformFee); setEditModePlatform(false); }}
                        saving={saving}
                    />
                    {editModePlatform ? (
                        <TextField size="small" type="number" value={platformFee}
                            onChange={e=>setPlatformFee(e.target.value)}
                            InputProps={{ endAdornment:<Box sx={{ fontSize:13,color:T.muted,pl:0.5 }}>%</Box>, sx:{ borderRadius:"12px" } }}
                            inputProps={{ min:0,max:100,step:"0.1" }} sx={{ width:200 }} />
                    ) : (
                        <StatPill value={platformFee} unit="%" />
                    )}
                </SCard>

                {/* GST */}
                <SCard accent={T.indigo}>
                    <SectionHeader
                        icon={<PaymentRoundedIcon />} iconBg={T.indigoLt} iconColor={T.indigo}
                        title="GST (%)"
                        subtitle="Goods and Services Tax percentage applied to each transaction."
                        editMode={editModeGst}
                        onEdit={()=>setEditModeGst(true)}
                        onSave={()=>handleSave("gst_percent",gstFee,setEditModeGst)}
                        onCancel={()=>{ setGstFee(savedGstFee); setEditModeGst(false); }}
                        saving={saving}
                    />
                    {editModeGst ? (
                        <TextField size="small" type="number" value={gstFee}
                            onChange={e=>setGstFee(e.target.value)}
                            InputProps={{ endAdornment:<Box sx={{ fontSize:13,color:T.muted,pl:0.5 }}>%</Box>, sx:{ borderRadius:"12px" } }}
                            inputProps={{ min:0,max:100,step:"0.1" }} sx={{ width:200 }} />
                    ) : (
                        <StatPill value={gstFee} unit="%" />
                    )}
                </SCard>
            </Box>

            {/* ── Lightweight Package Minimum Fare ── */}
            <SCard accent={T.orange}>
                <SectionHeader
                    icon={<ShieldRoundedIcon />} iconBg={T.orangeLt} iconColor={T.orange}
                    title="Lightweight Package Minimum Fare"
                    subtitle="When a package weight is below the threshold, the platform minimum fare is applied instead of the calculated price."
                    editMode={editModeMinFare}
                    onEdit={()=>setEditModeMinFare(true)}
                    onSave={handleSaveMinFare}
                    onCancel={()=>{ setMinFareWeightThreshold(savedMinFareWeight); setMinFareAmount(savedMinFareAmount); setEditModeMinFare(false); }}
                    saving={saving}
                />

                {editModeMinFare ? (
                    <Box sx={{ display:"flex",gap:2,flexWrap:"wrap" }}>
                        <TextField label="Weight Threshold (kg)" size="small" type="number"
                            value={minFareWeightThreshold}
                            onChange={e=>setMinFareWeightThreshold(e.target.value)}
                            helperText="e.g. 0.5 for packages under 500g"
                            inputProps={{ min:0,step:"0.1" }}
                            InputProps={{ sx:{ borderRadius:"12px" } }} sx={{ width:230 }} />
                        <TextField label="Minimum Fare (₹)" size="small" type="number"
                            value={minFareAmount}
                            onChange={e=>setMinFareAmount(e.target.value)}
                            helperText="Minimum charge for lightweight packages"
                            inputProps={{ min:0,step:"1" }}
                            InputProps={{ sx:{ borderRadius:"12px" } }} sx={{ width:230 }} />
                    </Box>
                ) : (
                    <Box sx={{ display:"flex",gap:3,flexWrap:"wrap",alignItems:"center" }}>
                        <Box>
                            <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px",mb:0.5 }}>Weight Threshold</Typography>
                            <StatPill value={minFareWeightThreshold||"—"} unit="kg" />
                        </Box>
                        <Box sx={{ fontSize:20,color:T.muted,mt:2 }}>→</Box>
                        <Box>
                            <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px",mb:0.5 }}>Minimum Fare</Typography>
                            <StatPill value={minFareAmount ? `₹${minFareAmount}` : "—"} unit="" />
                        </Box>
                        {minFareWeightThreshold && minFareAmount && (
                            <Box sx={{ ml:"auto",px:2,py:1,bgcolor:T.orangeLt,borderRadius:"12px",border:`1px solid ${T.orange}33` }}>
                                <Typography sx={{ fontSize:12,fontWeight:600,color:T.orange }}>
                                    Packages under {minFareWeightThreshold} kg → min ₹{minFareAmount}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </SCard>

            {/* ══ HIDDEN SECTIONS — old pricing model, kept for reference ══ */}
            {false && (<>
            {/* Floor & Ceiling Guardrails */}
            <Box mt={2}>
                <SCard accent={T.rose}>
                    <SectionHeader
                        icon={<ShieldRoundedIcon />} iconBg={T.roseLt} iconColor={T.rose}
                        title="Floor & Ceiling Guardrails"
                        subtitle="System-enforced price per km limits. Trip creation is blocked if the owner's rate falls outside these bounds."
                        editMode={editModeGuardrail}
                        onEdit={()=>setEditModeGuardrail(true)}
                        onSave={handleSaveGuardrails}
                        onCancel={()=>setEditModeGuardrail(false)}
                        saving={saving}
                    />
                    <Box sx={{ display:"flex",flexWrap:"wrap",gap:3,alignItems:"center" }}>
                        <Box>
                            <Typography sx={{ fontSize:11,fontWeight:700,color:T.muted,mb:0.5 }}>Floor price (₹/km)</Typography>
                            <TextField size="small" type="number" placeholder="No floor" value={floorPrice}
                                onChange={e=>setFloorPrice(e.target.value)}
                                InputProps={{ readOnly:!editModeGuardrail, sx:{ borderRadius:"10px" } }}
                                inputProps={{ min:0,step:"0.01" }} sx={{ width:130 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize:11,fontWeight:700,color:T.muted,mb:0.5 }}>Ceiling price (₹/km)</Typography>
                            <TextField size="small" type="number" placeholder="No ceiling" value={ceilingPrice}
                                onChange={e=>setCeilingPrice(e.target.value)}
                                InputProps={{ readOnly:!editModeGuardrail, sx:{ borderRadius:"10px" } }}
                                inputProps={{ min:0,step:"0.01" }} sx={{ width:130 }} />
                        </Box>
                    </Box>
                </SCard>
            </Box>

            {/* Owner Pricing Controls */}
            <Box mt={2}>
                <SCard accent={T.amber}>
                    <SectionHeader
                        icon={<StorefrontRoundedIcon />} iconBg={T.amberLt} iconColor={T.amber}
                        title="Owner Pricing Controls"
                        subtitle="Allow truck owners to set custom per-km rates and enforce a minimum platform margin."
                        editMode={editModeOwner}
                        onEdit={()=>setEditModeOwner(true)}
                        onSave={handleSaveOwnerPricing}
                        onCancel={()=>setEditModeOwner(false)}
                        saving={saving}
                    />
                    <Box sx={{ display:"flex",flexWrap:"wrap",gap:3,alignItems:"center" }}>
                        <FormControlLabel
                            control={<Switch checked={ownerPricingEnabled} color="primary" disabled={!editModeOwner}
                                onChange={e=>editModeOwner&&setOwnerPricingEnabled(e.target.checked)} />}
                            label={<Typography sx={{ fontSize:13,fontWeight:600 }}>Allow owners to set custom pricing</Typography>}
                        />
                        <Box>
                            <Typography sx={{ fontSize:11,fontWeight:700,color:T.muted,mb:0.5 }}>Minimum platform margin (%)</Typography>
                            <TextField size="small" type="number" value={platformMinMargin}
                                onChange={e=>setPlatformMinMargin(e.target.value)}
                                InputProps={{ readOnly:!editModeOwner, sx:{ borderRadius:"10px" } }}
                                inputProps={{ min:0,max:100 }} sx={{ width:100 }} />
                        </Box>
                    </Box>
                </SCard>
            </Box>

            {/* Dynamic Pricing Controls */}
            <Box mt={2}>
                <SCard accent={T.emerald}>
                    <Box sx={{ display:"flex",alignItems:"center",gap:1.5,mb:2 }}>
                        <Box sx={{ width:36,height:36,borderRadius:"10px",bgcolor:T.emeraldLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
                            <TrendingUpRoundedIcon sx={{ fontSize:18,color:T.emerald }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Dynamic Pricing Controls</Typography>
                            <Typography sx={{ fontSize:12,color:T.muted,mt:0.2 }}>Automatically apply multipliers based on occupancy and time-to-departure.</Typography>
                        </Box>
                    </Box>
                    <FormControlLabel
                        control={<Switch checked={demandEnabled} color="primary"
                            onChange={e=>setDemandEnabled(e.target.checked)} />}
                        label={<Typography sx={{ fontSize:13,fontWeight:600 }}>Enable demand-based pricing globally</Typography>}
                        sx={{ mb:2 }} />
                    <Box sx={{ display:"flex",flexWrap:"wrap",gap:4 }}>
                        <Box sx={{ flex:"1 1 260px" }}>
                            <Typography sx={{ fontSize:13,fontWeight:700,color:T.text,mb:1 }}>Occupancy-Based Tiers</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ "& .MuiTableCell-root":{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",pb:0.5 } }}>
                                        <TableCell sx={{ pl:0 }}>Threshold (%)</TableCell>
                                        <TableCell>Multiplier (×)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[["demand_occ_tier2","demand_occ_mult_t2"],["demand_occ_tier3","demand_occ_mult_t3"],["demand_occ_tier4","demand_occ_mult_t4"]].map(([t,m])=>(
                                        <TableRow key={t}>
                                            <TableCell sx={{ pl:0,py:0.8 }}>{demandField(t)}</TableCell>
                                            <TableCell sx={{ py:0.8 }}>{demandField(m)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ flex:"1 1 260px" }}>
                            <Typography sx={{ fontSize:13,fontWeight:700,color:T.text,mb:1 }}>Time-to-Departure Tiers</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ "& .MuiTableCell-root":{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",pb:0.5 } }}>
                                        <TableCell sx={{ pl:0 }}>Within (hours)</TableCell>
                                        <TableCell>Multiplier (×)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[["demand_time_tier2_h","demand_time_mult_t2"],["demand_time_tier3_h","demand_time_mult_t3"],["demand_time_tier4_h","demand_time_mult_t4"]].map(([t,m])=>(
                                        <TableRow key={t}>
                                            <TableCell sx={{ pl:0,py:0.8 }}>{demandField(t)}</TableCell>
                                            <TableCell sx={{ py:0.8 }}>{demandField(m)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                    <Box sx={{ display:"flex",justifyContent:"flex-end",mt:2.5 }}>
                        <Button variant="contained" disableElevation onClick={handleSaveDemandConfig} disabled={saving}
                            startIcon={<SaveRoundedIcon sx={{ fontSize:15 }} />}
                            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk } }}>
                            Save Demand Config
                        </Button>
                    </Box>
                </SCard>
            </Box>

            {/* Cargo Surcharge Rates */}
            <Box mt={2}>
                <SCard accent={T.indigo}>
                    <SectionHeader
                        icon={<LocalShippingRoundedIcon />} iconBg={T.indigoLt} iconColor={T.indigo}
                        title="Cargo Surcharge Rates"
                        subtitle="Extra charges applied on top of base price for special cargo types."
                        editMode={editModeCargo}
                        onEdit={()=>setEditModeCargo(true)}
                        onSave={handleSaveCargoConfig}
                        onCancel={()=>setEditModeCargo(false)}
                        saving={saving}
                    />
                    <Table size="small" sx={{ maxWidth:500 }}>
                        <TableHead>
                            <TableRow sx={{ "& .MuiTableCell-root":{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",pb:0.5 } }}>
                                <TableCell sx={{ pl:0 }}>Cargo Type</TableCell>
                                <TableCell>Surcharge</TableCell>
                                <TableCell>Unit</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[
                                { label:"Fragile",    emoji:"🔮", key:"cargo_fragile_surcharge_pct",    unit:"% of base price" },
                                { label:"Hazardous",  emoji:"⚠️", key:"cargo_hazardous_surcharge_pct",  unit:"% of base price" },
                                { label:"Perishable", emoji:"🌡️", key:"cargo_perishable_rate_per_km",   unit:"₹ per km (flat)" },
                            ].map(r => (
                                <TableRow key={r.key}>
                                    <TableCell sx={{ pl:0,py:1 }}>
                                        <Typography sx={{ fontSize:13 }}>{r.emoji} {r.label}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ py:1 }}>
                                        <TextField size="small" type="number"
                                            value={cargoConfig[r.key]}
                                            onChange={e=>setCargoConfig(p=>({...p,[r.key]:e.target.value}))}
                                            InputProps={{ readOnly:!editModeCargo, sx:{ borderRadius:"9px" } }}
                                            inputProps={{ min:0,step:"0.1" }} sx={{ width:90 }} />
                                    </TableCell>
                                    <TableCell sx={{ py:1,fontSize:12,color:T.muted }}>{r.unit}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SCard>
            </Box>
            </>)}
            {/* ══ END HIDDEN SECTIONS ══ */}

            <Snackbar open={snackbar.open} autoHideDuration={3500}
                onClose={()=>setSnackbar(s=>({...s,open:false}))}
                anchorOrigin={{ vertical:"top",horizontal:"center" }}>
                <Alert severity={snackbar.severity} variant="filled"
                    onClose={()=>setSnackbar(s=>({...s,open:false}))}
                    sx={{ borderRadius:"12px" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default BillingSettings;
